import { ObservableSet, runInAction } from "mobx";
import { useContext, useEffect, useState } from "react";

import useRouter from "@vromo/hooks/useRouter";
import { useStore } from "@vromo/mobx-react/mobx-react";
import { Dispatch, DispatchStatus, EntityList, Job, Vehicle } from "@vromo/models";
import { useToasts } from "@vromo/ui/components/Toasts";
import { PageContext } from "../../stores/local/PageStore";
import { RootStore } from "../../stores/RootStore";
import Tracking from '@vromo/analytics';
import { StringNumber } from "@vromo/models/helpers/models";
import { useRouteMatch } from "react-router";

/** only inpext and dispatch works */
type JobSelection = "inspect" | "cancel" | "recall" | "addIssue" | "expanded" | "duplicate" | "dispatch"

/** Leverage Dispatch Page MOBx state context and common functions
 * @note requires container component to be mobx "observer"
 */
export const useDispatchPage = () => {

    const {
        jobStore: { loaded: jobsLoaded },
        authStore: { graph, role, loaded: authLoaded },
        vehicleStore,
        vehicleStore: { loaded: vehiclesLoaded },
        groupStore,
        groupStore: { loaded: groupsLoaded }
    } = useStore<RootStore>('rootStore');
    const { settings, pageLoading, setLoading } = useContext(PageContext)
    const [isLoading, setPageIsLoading] = useState(false)
    const showToast = useToasts();
    const { history } = useRouter();
    const isLiveSiteView = useRouteMatch('/live-site/:auth') !== null;


    useEffect(() => {
        if (isLiveSiteView)
            setPageIsLoading(
                !jobsLoaded ||
                !vehiclesLoaded ||
                pageLoading)
        else
            setPageIsLoading(
                !jobsLoaded ||
                !authLoaded ||
                !vehiclesLoaded ||
                !groupsLoaded ||
                pageLoading)
    }, [jobsLoaded, authLoaded, vehiclesLoaded, groupsLoaded, pageLoading, isLiveSiteView])

    const jobSelection = (group: JobSelection) =>
        settings[group] ? settings[group] as Set<number> : new ObservableSet<number>()

    const firstJobSelected = (group: JobSelection) =>
        jobSelection(group).keys().next().value

    const areJobsSelected = (group: JobSelection) => jobSelection(group).size > 0

    const updateJobSelection = (group: JobSelection, update: "add" | "delete" | "clear", id?: number | null) => {
        switch (update) {
            case 'add':
                if (id)
                    runInAction(() => settings[group] = jobSelection(group).add(id!))
                break;
            case 'delete':
                if (id)
                    runInAction(() => settings[group] = jobSelection(group).delete(id! as number))
                break;
            case 'clear':
                runInAction(() => settings[group] = new Set<number>())
                break;
            default:
                throw new Error(`invalid operation type on group '${group}'`)
        }
    }

    /** change current window, while in the dispatch page */
    const goToWindow = (destination: "editJob" | "openTracking", resource?: StringNumber): void => {
        const err = () => { throw new Error(`invalid arguments for 'goToWindow' ${destination}`) }

        switch (destination) {
            case "editJob":
                if (!resource)
                    err()
                history.push(`/dispatch/edit/${resource}`)
                break;

            case "openTracking":
                if (!resource?.toString()?.length)
                    showToast(
                        "No tracking link for the target job",
                        'warning'
                    )
                else {
                    const win = window.open(resource!.toString(), '_blank');
                    win?.focus();
                }
                break;

            default:
                err()
        }

    }

    const dispatchJobs = async (jobs: Job[], types: DispatchStatus[], vehicle: Vehicle, onError?: (msg: string) => void) => {
        try {
            setLoading(true)

            const transformedJobs = jobs.map((job) => {
                const dispatches = new EntityList(Dispatch)
                // if the job is already dispatched, return it first
                job.dispatched && dispatches.push({ type: 'returned', vehicle: { id: job.vehicle } })
                dispatches.push(...types.map(type => ({ type, vehicle: { id: vehicle.id! } })))
                return ({
                    id: job.id,
                    dispatches
                }) as Partial<Job>
            })

            await graph.root
                .graph()
                .roles(role.id)
                .jobs()
                .post(transformedJobs);

            showToast(
                `${jobs.length} Job${jobs.length > 1 ? "s" : ""} assigned to driver '${vehicle.displayName(vehicleStore.driverVehicleMap, groupStore.withId(vehicle.group!))}'`,
                'success'
            );

            Tracking.track(`${jobs.length} Job(s) Dispatched (assigned)`);
        }
        catch (err) {
            console.error(err)
            showToast(
                'Something went wrong while attempting to assign job(s)',
                "error"
            );
            onError && onError('Something went wrong while attempting to assign this job(s)')
        }
        finally {
            setLoading(false)
        }
    }

    /** for provided jobs, perform a new offer */
    const offerJobs = async (jobs: Job[], vehicles: Vehicle[], onError?: (msg: string) => void): Promise<void> => {

        try {
            setLoading(true)
            const offeringTo = vehicles.map(({ id }) => ({ id: id! }))

            await Promise.all(jobs.map(({ id }) => graph.root.
                graph()
                .roles(role.id)
                .jobs(id!)
                .offered()
                .post(offeringTo)
            ))

            showToast(
                `${jobs.length} Job${jobs.length > 1 ? "s" : ""} assigned to ${vehicles.length} driver${vehicles.length > 1 ? "s" : ""}`,
                'success'
            );

            Tracking.track(`${jobs.length} Job(s) assigned (offered)`);
        }
        catch (err) {
            console.error(err)
            showToast(
                'Something went wrong while attempting to offer driver(s) to the job(s)',
                "error"
            );
            onError && onError('Something went wrong while attempting to offer driver(s) to the job(s)')
        }
        finally {
            setLoading(false)
        }
        return
    }

    return {
        /** current job ids selected for a given category 
         * @note only 'inspect' and 'dispatch' are implemented
        */
        jobSelection,
        areJobsSelected,
        firstJobSelected,
        /** set current job ids selected for a given category
         * @note only 'inspect' and 'dispatch' are implemented
         */
        updateJobSelection,
        /** whether websocket data or rest query are being processed */
        isLoading,
        setLoading,
        dispatchJobs,
        offerJobs,
        goToWindow,
        isLiveSiteView
    }

}